import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import classNames from 'classnames';

const config = require('config/app.config');

declare global {
    interface Window {
        FB: any;
        fbAsyncInit: () => void;
    }
}

type FacebookSignInButtonProps = {
    onSuccess: (accessToken: string) => void;
    onError?: (error: string) => void;
    className?: string;
    disabled?: boolean;
    text?: string;
};

function FacebookSignInButton({
    onSuccess,
    onError,
    className,
    disabled = false,
    text = 'Continue with Facebook',
}: FacebookSignInButtonProps) {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isFacebookReady, setIsFacebookReady] = useState(false);
    const initializationAttemptedRef = useRef(false);

    useEffect(() => {
        const facebookAppId = config.facebook.app_id;

        if (!facebookAppId) {
            console.warn('Facebook App ID not configured');
            if (onError) {
                onError('Facebook authentication is not configured');
            }
            return;
        }

        // Check if Facebook SDK is already loaded and initialized
        if (window.FB && window.FB.getAuthResponse) {
            setIsFacebookReady(true);
            return;
        }

        // Prevent multiple initialization attempts
        if (initializationAttemptedRef.current) {
            return;
        }

        initializationAttemptedRef.current = true;

        // Check if script already exists
        const existingScript = document.querySelector('script[src*="connect.facebook.net"]');

        if (existingScript && window.FB) {
            // Script exists and FB is available, try to initialize
            try {
                window.FB.init({
                    appId: facebookAppId,
                    cookie: true,
                    xfbml: true,
                    version: 'v18.0'
                });
                setIsFacebookReady(true);
            } catch (e) {
                console.error('Facebook initialization error:', e);
            }
            return;
        }

        // Define fbAsyncInit before loading the script
        window.fbAsyncInit = () => {
            try {
                window.FB.init({
                    appId: facebookAppId,
                    cookie: true,
                    xfbml: true,
                    version: 'v18.0'
                });
                setIsFacebookReady(true);
            } catch (e) {
                console.error('Facebook initialization error:', e);
                if (onError) {
                    onError('Failed to initialize Facebook SDK');
                }
            }
        };

        // Load Facebook SDK script
        const script = document.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';

        script.onload = () => {
            // Check if FB was initialized via fbAsyncInit
            // If not, wait a bit and check again
            if (!window.FB) {
                const checkInterval = setInterval(() => {
                    if (window.FB) {
                        clearInterval(checkInterval);
                        // Force initialization if fbAsyncInit wasn't called
                        if (!window.FB.getAuthResponse) {
                            try {
                                window.FB.init({
                                    appId: facebookAppId,
                                    cookie: true,
                                    xfbml: true,
                                    version: 'v18.0'
                                });
                            } catch (e) {
                                console.error('Facebook initialization error:', e);
                            }
                        }
                        setIsFacebookReady(true);
                    }
                }, 100);

                // Clear interval after 5 seconds
                setTimeout(() => clearInterval(checkInterval), 5000);
            }
        };

        script.onerror = () => {
            console.error('Failed to load Facebook SDK');
            if (onError) {
                onError('Failed to load Facebook authentication');
            }
            initializationAttemptedRef.current = false;
        };

        if (!existingScript) {
            document.body.appendChild(script);
        }
    }, [onError]);

    const handleFacebookLogin = () => {
        if (!window.FB) {
            if (onError) {
                onError('Facebook SDK not loaded');
            }
            return;
        }

        if (disabled) {
            return;
        }

        window.FB.login((response: any) => {
            if (response.authResponse) {
                const accessToken = response.authResponse.accessToken;
                onSuccess(accessToken);
            } else {
                if (onError) {
                    const errorMessage = response.error?.message || 'Facebook login cancelled or failed';
                    onError(errorMessage);
                }
            }
        }, {
            scope: 'email,public_profile',
            return_scopes: true
        });
    };

    if (!config.facebook.app_id) {
        return (
            <Button
                fullWidth
                variant="outlined"
                disabled={true}
                sx={{
                    textTransform: 'none',
                    borderColor: '#dadce0',
                    color: '#3c4043',
                }}
            >
                {text}
            </Button>
        );
    }

    return (
        <Button
            ref={buttonRef}
            fullWidth
            variant="outlined"
            disabled={disabled || !isFacebookReady}
            onClick={handleFacebookLogin}
            className={classNames(className)}
            sx={{
                textTransform: 'none',
                borderColor: '#1877f2',
                color: '#1877f2',
                '&:hover': {
                    borderColor: '#166fe5',
                    backgroundColor: 'rgba(24, 119, 242, 0.04)',
                },
                '&:disabled': {
                    borderColor: '#dadce0',
                    color: '#dadce0',
                },
            }}
            startIcon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
            }
        >
            {text}
        </Button>
    );
}

export default FacebookSignInButton;

